<template>
  <div class="wysiwyg-editor-shell">
    <section class="wysiwyg-main">
      <div class="admin-panel wysiwyg-settings-bar">
        <div class="admin-panel-head">
          <h3>文章设置</h3>
          <span>基础信息</span>
        </div>

        <div class="admin-form-grid">
          <label class="admin-field admin-field-full">
            <span>文章标题</span>
            <input
              :value="note.title"
              type="text"
              placeholder="请直接在这里修改文章标题"
              @input="emitPatch({ title: $event.target.value })"
            >
          </label>
          <label class="admin-field">
            <span>分类名称</span>
            <input
              :value="note.categoryTitle"
              type="text"
              placeholder="例如：前端排版"
              @input="emitPatch({ categoryTitle: $event.target.value })"
            >
          </label>

          <label class="admin-field">
            <span>封面地址</span>
            <input
              :value="note.image"
              type="text"
              placeholder="/uploads/images/..."
              @input="emitPatch({ image: $event.target.value })"
            >
          </label>

          <label class="admin-field">
            <span>日期</span>
            <input :value="note.date" type="date" @input="emitPatch({ date: $event.target.value })">
          </label>

          <label class="admin-field">
            <span>阅读时间</span>
            <input
              :value="note.reading"
              type="text"
              placeholder="阅读时间 6 分钟"
              @input="emitPatch({ reading: $event.target.value })"
            >
          </label>

          <label class="admin-field admin-field-full">
            <span>列表摘要</span>
            <textarea
              :value="note.summary"
              rows="4"
              placeholder="这里会显示在技术手记列表页"
              @input="emitPatch({ summary: $event.target.value })"
            ></textarea>
          </label>
        </div>
      </div>

      <article class="article-hero wysiwyg-hero">
        <div class="article-hero-copy">
          <span class="article-eyebrow">{{ note.categoryTitle || "技术手记" }}</span>

          <textarea
            class="wysiwyg-title"
            :value="note.title"
            rows="2"
            placeholder="请在这里输入标题"
            @input="emitPatch({ title: $event.target.value })"
          ></textarea>

          <textarea
            class="wysiwyg-summary"
            :value="note.heroSummary"
            rows="4"
            placeholder="先用一两段话告诉读者，这篇文章适合谁、会讲什么、看完能得到什么。"
            @input="emitPatch({ heroSummary: $event.target.value })"
          ></textarea>

          <div class="article-meta">
            <span>{{ note.categoryTitle || "未分类" }}</span>
            <span>{{ note.date || "未设置日期" }}</span>
            <span>{{ note.reading || "阅读时间未填写" }}</span>
          </div>
        </div>

        <div class="wysiwyg-cover-panel">
          <div class="article-cover wysiwyg-cover">
            <img :src="note.image" :alt="note.title || '文章封面'">
          </div>

          <div class="wysiwyg-cover-actions">
            <label class="admin-upload-button">
              <input type="file" accept="image/*" @change="handleCoverUpload">
              上传封面
            </label>
            <button
              type="button"
              class="admin-secondary-button"
              @click="emitPatch({ image: '/images/portal-info.webp' })"
            >
              恢复默认封面
            </button>
          </div>
        </div>
      </article>

      <article class="article-reader-card wysiwyg-article-card">
        <div class="article-reader-body">
          <div
            ref="editorBodyRef"
            class="article-rich-content wysiwyg-body"
            contenteditable="true"
            spellcheck="true"
            @input="handleBodyInput"
            @keydown="handleBodyKeydown"
            @click="handleBodyClick"
          ></div>
        </div>
      </article>
    </section>

    <aside class="wysiwyg-sidepanel">
      <section class="admin-panel">
        <div class="admin-panel-head">
          <h3>编辑工具</h3>
          <span>正文格式与组件</span>
        </div>

        <div class="wysiwyg-tool-section">
          <span class="wysiwyg-tool-label">文字格式</span>
          <div class="wysiwyg-toolbar__group">
            <button type="button" class="admin-chip-button" @click="applyBlock('p')">正文</button>
            <button type="button" class="admin-chip-button" @click="applyBlock('h2')">二级标题</button>
            <button type="button" class="admin-chip-button" @click="applyBlock('h3')">三级标题</button>
            <button type="button" class="admin-chip-button" @click="toggleBulletList">列表</button>
            <button type="button" class="admin-chip-button" @click="runCommand('bold')">加粗</button>
            <button type="button" class="admin-chip-button" @click="runCommand('underline')">下划线</button>
            <button type="button" class="admin-chip-button" @click="applyFontSize(2)">小号</button>
            <button type="button" class="admin-chip-button" @click="applyFontSize(3)">标准</button>
            <button type="button" class="admin-chip-button" @click="applyFontSize(5)">大号</button>
            <button type="button" class="admin-chip-button" @click="runCommand('removeFormat')">清除样式</button>
          </div>
        </div>

        <div class="wysiwyg-tool-section">
          <span class="wysiwyg-tool-label">插入组件</span>
          <div class="wysiwyg-toolbar__group">
            <button type="button" class="admin-secondary-button" @click="focusBody">继续编辑正文</button>
            <button type="button" class="admin-secondary-button" @click="insertCallout">插入提示块</button>
            <button type="button" class="admin-secondary-button" @click="insertCodeBlock">插入代码块</button>
            <button type="button" class="admin-secondary-button" @click="triggerImageUpload">插入图片</button>
            <button type="button" class="admin-secondary-button" @click="triggerVideoUpload">插入视频</button>
            <button type="button" class="admin-secondary-button" @click="insertVideoByUrl">视频链接</button>
          </div>
        </div>

        <p class="admin-section-tip">
          中间正文区就是正式文章画布。点击进去直接写，右侧工具只负责插入组件和调整样式。
        </p>

        <input
          ref="inlineImageInputRef"
          class="wysiwyg-hidden-input"
          type="file"
          accept="image/*"
          @change="handleInlineImageUpload"
        >
        <input
          ref="inlineVideoInputRef"
          class="wysiwyg-hidden-input"
          type="file"
          accept="video/*"
          @change="handleInlineVideoUpload"
        >
      </section>

      <section v-if="selectedMediaState" class="admin-panel">
        <div class="admin-panel-head">
          <h3>媒体设置</h3>
          <span>{{ selectedMediaState.type === 'image' ? '图片' : '视频' }}</span>
        </div>

        <label class="admin-field">
          <span>显示宽度</span>
          <input
            class="wysiwyg-range"
            type="range"
            min="20"
            max="100"
            step="1"
            :value="selectedMediaState.width"
            :disabled="selectedMediaState.inRow"
            @input="updateSelectedMediaWidth($event.target.value)"
          >
        </label>

        <div class="wysiwyg-tool-section">
          <span class="wysiwyg-tool-label">对齐方式</span>
          <div class="wysiwyg-toolbar__group">
            <button
              type="button"
              class="admin-chip-button"
              :class="{ 'is-active': selectedMediaState.align === 'left' }"
              @click="updateSelectedMediaAlign('left')"
            >
              居左
            </button>
            <button
              type="button"
              class="admin-chip-button"
              :class="{ 'is-active': selectedMediaState.align === 'center' }"
              @click="updateSelectedMediaAlign('center')"
            >
              居中
            </button>
            <button
              type="button"
              class="admin-chip-button"
              :class="{ 'is-active': selectedMediaState.align === 'right' }"
              @click="updateSelectedMediaAlign('right')"
            >
              居右
            </button>
          </div>
        </div>

        <div class="wysiwyg-tool-section">
          <span class="wysiwyg-tool-label">排版方式</span>
          <div class="wysiwyg-toolbar__group">
            <button
              type="button"
              class="admin-chip-button"
              :disabled="selectedMediaState.inRow"
              @click="groupSelectedMediaWithPrevious"
            >
              与上一张并排
            </button>
            <button
              type="button"
              class="admin-chip-button"
              :disabled="!selectedMediaState.inRow"
              @click="splitSelectedMediaRow"
            >
              拆回单列
            </button>
          </div>
        </div>

        <p class="admin-section-tip">
          先点正文里的图片或视频，再在这里调整尺寸和位置。要做左右并排时，选中后一键点“与上一张并排”会比卡光标更稳。
          <span v-if="selectedMediaState.inRow"> 当前媒体已经在双栏里，宽度会自动占满所在栏位。</span>
        </p>
      </section>

      <button type="button" class="admin-inline-danger wysiwyg-remove-note" @click="$emit('remove-note')">
        删除这篇文章
      </button>
    </aside>
  </div>
</template>

<script setup>
import { nextTick, ref, watch } from "vue";
import { useContentStore } from "@/store/contentStore";

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["patch-note", "remove-note"]);

const {
  uploadMediaFile,
  renderContentBlocksToHtml,
  htmlToContentBlocks,
} = useContentStore();

const editorBodyRef = ref(null);
const inlineImageInputRef = ref(null);
const inlineVideoInputRef = ref(null);
const selectedMediaElementRef = ref(null);
const selectedMediaState = ref(null);

const emitPatch = (patch) => {
  emit("patch-note", patch);
};

const countCodeBlocksFromHtml = (html = "") =>
  (String(html || "").match(/class="article-code"/g) || []).length;

const getEditorHtml = () => {
  const html = String(
    props.note.contentHtml || renderContentBlocksToHtml(props.note.contentBlocks || [])
  ).trim();

  if (!html || html.includes('class="article-empty"')) {
    return "<p>从这里开始写正文。</p>";
  }

  return html;
};

const normalizeEditorHtml = (rawHtml = "") => {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = String(rawHtml || "");

  const childNodes = Array.from(wrapper.childNodes);
  childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      const paragraph = document.createElement("p");
      paragraph.textContent = node.textContent.trim();
      wrapper.replaceChild(paragraph, node);
      return;
    }

    if (!(node instanceof HTMLElement)) {
      return;
    }

    if (
      node.tagName === "DIV" &&
      !node.classList.contains("article-callout") &&
      !node.classList.contains("article-video-frame")
    ) {
      const paragraph = document.createElement("p");
      paragraph.innerHTML = node.innerHTML;
      wrapper.replaceChild(paragraph, node);
    }
  });

  const text = wrapper.textContent?.replace(/\u00a0/g, " ").trim() || "";
  const hasMedia = wrapper.querySelector("img,video,iframe,pre,figure,.article-callout,.article-video");
  return text || hasMedia ? wrapper.innerHTML.trim() : "";
};

const clearSelectedMedia = () => {
  if (selectedMediaElementRef.value) {
    selectedMediaElementRef.value.classList.remove("is-selected");
  }

  selectedMediaElementRef.value = null;
  selectedMediaState.value = null;
};

const readMediaWidth = (element) => {
  const currentWidth = Number.parseInt(
    element?.style?.width || element?.dataset?.width || "100",
    10
  );
  return Number.isFinite(currentWidth) ? currentWidth : 100;
};

const readMediaAlign = (element) => {
  const align = element?.dataset?.align;
  return align === "left" || align === "right" ? align : "center";
};

const isMediaElement = (element) =>
  element instanceof HTMLElement &&
  (element.classList.contains("article-figure") || element.classList.contains("article-video"));

const isMediaRow = (element) =>
  element instanceof HTMLElement && element.classList.contains("article-media-row");

const buildSelectedMediaState = (element, type) => ({
  type,
  width: readMediaWidth(element),
  align: readMediaAlign(element),
  inRow: isMediaRow(element?.parentElement),
});

const selectMedia = (element, type) => {
  if (!(element instanceof HTMLElement)) {
    clearSelectedMedia();
    return;
  }

  clearSelectedMedia();
  element.classList.add("is-selected");
  selectedMediaElementRef.value = element;
  selectedMediaState.value = buildSelectedMediaState(element, type);
  placeCaretAfterNode(element);
};

const syncBodyFromNote = async () => {
  await nextTick();
  if (!editorBodyRef.value) {
    return;
  }

  editorBodyRef.value.innerHTML = getEditorHtml();
  clearSelectedMedia();
};

const focusBody = async () => {
  await nextTick();
  editorBodyRef.value?.focus();
};

const emitBodyPatch = () => {
  const html = normalizeEditorHtml(editorBodyRef.value?.innerHTML || "");

  emitPatch({
    contentHtml: html,
    contentBlocks: html ? htmlToContentBlocks(html) : [],
    codeBlocks: countCodeBlocksFromHtml(html),
  });
};

watch(
  () => props.note.id,
  () => {
    syncBodyFromNote();
  },
  { immediate: true }
);

const handleBodyInput = () => {
  emitBodyPatch();
};

const getSelectionContext = () => {
  const selection = window.getSelection();
  const focusNode = selection?.focusNode;
  const focusElement = focusNode instanceof Element ? focusNode : focusNode?.parentElement;
  return {
    selection,
    focusElement: focusElement || null,
    listItem: focusElement?.closest("li") || null,
    listRoot: focusElement?.closest("ul, ol") || null,
  };
};

const placeCaretAtStart = (element) => {
  const selection = window.getSelection();
  if (!selection || !element) {
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

const placeCaretAfterNode = (element) => {
  const selection = window.getSelection();
  if (!selection || !element?.parentNode) {
    return;
  }

  const range = document.createRange();
  range.setStartAfter(element);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

const exitListAtCurrentItem = () => {
  const { listItem, listRoot } = getSelectionContext();
  if (!listItem || !listRoot) {
    return false;
  }

  const itemText = listItem.textContent?.replace(/\u00a0/g, " ").trim() || "";
  if (itemText) {
    return false;
  }

  const paragraph = document.createElement("p");
  paragraph.innerHTML = "<br>";
  listRoot.parentNode?.insertBefore(paragraph, listRoot.nextSibling);
  listItem.remove();

  if (!listRoot.querySelector("li")) {
    listRoot.remove();
  }

  placeCaretAtStart(paragraph);
  emitBodyPatch();
  return true;
};

const handleBodyKeydown = (event) => {
  if (event.key !== "Enter" || event.shiftKey) {
    return;
  }

  const { listItem, focusElement } = getSelectionContext();
  if (listItem) {
    if (exitListAtCurrentItem()) {
      event.preventDefault();
    }
    return;
  }

  if (focusElement?.closest("pre")) {
    return;
  }

  event.preventDefault();
  document.execCommand("insertParagraph");
};

const getEditorRange = () => {
  const editor = editorBodyRef.value;
  if (!editor) {
    return null;
  }

  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    if (editor.contains(container)) {
      return range;
    }
  }

  const fallback = document.createRange();
  fallback.selectNodeContents(editor);
  fallback.collapse(false);
  return fallback;
};

const moveRangeAfterTopLevelBlock = (range) => {
  const editor = editorBodyRef.value;
  const container =
    range.commonAncestorContainer instanceof Element
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;

  if (!editor || !container) {
    return range;
  }

  const blockRoot =
    container.closest("ul, ol") ||
    container.closest("p, h2, h3, figure, section.article-code, section.article-video, div.article-callout");

  if (blockRoot && editor.contains(blockRoot)) {
    const newRange = document.createRange();
    newRange.setStartAfter(blockRoot);
    newRange.collapse(true);
    return newRange;
  }

  return range;
};

const insertHtmlAtCursor = async (html, options = {}) => {
  await focusBody();
  const selection = window.getSelection();
  let range = getEditorRange();
  if (!range || !selection) {
    return;
  }

  if (options.afterSelectedMedia && selectedMediaElementRef.value?.parentNode) {
    const anchor = selectedMediaElementRef.value;
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    const nodes = Array.from(template.content.childNodes);
    anchor.insertAdjacentHTML("afterend", html.trim());

    const lastInserted = nodes.length
      ? anchor.parentNode?.childNodes[
        Array.prototype.indexOf.call(anchor.parentNode.childNodes, anchor) + nodes.length
      ]
      : anchor.nextSibling;

    if (lastInserted) {
      const newRange = document.createRange();
      newRange.setStartAfter(lastInserted);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    emitBodyPatch();
    return;
  }

  if (options.asBlock) {
    range = moveRangeAfterTopLevelBlock(range);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  range.deleteContents();
  const template = document.createElement("template");
  template.innerHTML = html.trim();
  const fragment = template.content;
  const lastNode = fragment.lastChild;
  range.insertNode(fragment);

  if (lastNode) {
    range.setStartAfter(lastNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  emitBodyPatch();
};

const runCommand = async (command) => {
  await focusBody();
  document.execCommand(command);
  emitBodyPatch();
};

const applyFontSize = async (size) => {
  await focusBody();
  document.execCommand("styleWithCSS", false, true);
  document.execCommand("fontSize", false, String(size));
  emitBodyPatch();
};

const applyBlock = async (tagName) => {
  await focusBody();
  document.execCommand("formatBlock", false, tagName);
  emitBodyPatch();
};

const toggleBulletList = async () => {
  await focusBody();
  document.execCommand("insertUnorderedList");
  emitBodyPatch();
};

const insertCallout = async () => {
  await insertHtmlAtCursor(
    `
      <div class="article-callout is-info">
        <strong>提示标题</strong>
        <p>这里填写你想强调的说明内容。</p>
      </div>
      <p><br></p>
    `,
    { asBlock: true, afterSelectedMedia: true }
  );
};

const insertCodeBlock = async () => {
  await insertHtmlAtCursor(
    `
      <section class="article-code">
        <div class="article-code-head">
          <span>example.js</span>
          <button type="button" data-copy-code="console.log(&quot;Hello, world!&quot;);">复制代码</button>
        </div>
        <pre><code>console.log("Hello, world!");</code></pre>
      </section>
      <p><br></p>
    `,
    { asBlock: true, afterSelectedMedia: true }
  );
};

const getMediaInsertPreset = () => {
  const width = selectedMediaState.value?.width;
  const align = selectedMediaState.value?.align;

  return {
    width: Number.isFinite(width) ? width : 100,
    align: align === "left" || align === "right" ? align : "center",
  };
};

const triggerImageUpload = () => {
  inlineImageInputRef.value?.click();
};

const triggerVideoUpload = () => {
  inlineVideoInputRef.value?.click();
};

const handleInlineImageUpload = async (event) => {
  const file = event.target?.files?.[0];
  if (!file) {
    return;
  }

  const result = await uploadMediaFile(file);
  if (result.ok) {
    const preset = getMediaInsertPreset();
    await insertHtmlAtCursor(
      `
        <figure class="article-figure" data-width="${preset.width}" data-align="${preset.align}" style="width: ${preset.width}%;">
          <img
            src="${result.data.url}"
            alt="${file.name.replace(/\.[^.]+$/, "")}"
            style="width: 100%; max-width: 100%; display: block;"
          >
        </figure>
      `,
      { asBlock: true, afterSelectedMedia: true }
    );
  }

  event.target.value = "";
};

const handleInlineVideoUpload = async (event) => {
  const file = event.target?.files?.[0];
  if (!file) {
    return;
  }

  const result = await uploadMediaFile(file);
  if (result.ok) {
    const preset = getMediaInsertPreset();
    await insertHtmlAtCursor(
      `
        <section class="article-video" data-width="${preset.width}" data-align="${preset.align}" style="width: ${preset.width}%;">
          <div class="article-video-frame">
            <video class="article-video-player" controls preload="metadata" src="${result.data.url}"></video>
          </div>
        </section>
      `,
      { asBlock: true, afterSelectedMedia: true }
    );
  }

  event.target.value = "";
};

const insertVideoByUrl = async () => {
  const url = window.prompt("请输入视频地址");
  if (!url) {
    return;
  }

  const preset = getMediaInsertPreset();
  await insertHtmlAtCursor(
    `
      <section class="article-video" data-width="${preset.width}" data-align="${preset.align}" style="width: ${preset.width}%;">
        <div class="article-video-frame">
          <video class="article-video-player" controls preload="metadata" src="${url}"></video>
        </div>
      </section>
    `,
    { asBlock: true, afterSelectedMedia: true }
  );
};

const handleCoverUpload = async (event) => {
  const file = event.target?.files?.[0];
  if (!file) {
    return;
  }

  const result = await uploadMediaFile(file);
  if (result.ok) {
    emitPatch({ image: result.data.url });
  }

  event.target.value = "";
};

const handleBodyClick = (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    clearSelectedMedia();
    return;
  }

  const figure = target.closest(".article-figure");
  if (figure instanceof HTMLElement) {
    selectMedia(figure, "image");
    return;
  }

  const video = target.closest(".article-video");
  if (video instanceof HTMLElement) {
    selectMedia(video, "video");
    return;
  }

  clearSelectedMedia();
};

const updateSelectedMediaWidth = (value) => {
  const width = Math.max(20, Math.min(100, Number.parseInt(value, 10) || 100));
  const element = selectedMediaElementRef.value;
  if (!(element instanceof HTMLElement)) {
    return;
  }

  element.dataset.width = String(width);
  element.style.width = `${width}%`;
  element.style.maxWidth = "100%";

  if (selectedMediaState.value?.type === "image") {
    const image = element.querySelector("img");
    if (image instanceof HTMLImageElement) {
      image.style.width = "100%";
      image.style.maxWidth = "100%";
      image.style.display = "block";
    }
  }

  selectedMediaState.value = {
    ...selectedMediaState.value,
    width,
  };
  emitBodyPatch();
};

const updateSelectedMediaAlign = (align) => {
  const element = selectedMediaElementRef.value;
  if (!(element instanceof HTMLElement)) {
    return;
  }

  element.dataset.align = align;
  selectedMediaState.value = {
    ...selectedMediaState.value,
    align,
  };
  emitBodyPatch();
};

const groupSelectedMediaWithPrevious = () => {
  const current = selectedMediaElementRef.value;
  if (!isMediaElement(current) || isMediaRow(current.parentElement)) {
    return;
  }

  let previous = current.previousElementSibling;
  while (previous && !isMediaElement(previous)) {
    previous = previous.previousElementSibling;
  }

  if (!isMediaElement(previous) || !current.parentNode) {
    return;
  }

  const row = document.createElement("div");
  row.className = "article-media-row";
  current.parentNode.insertBefore(row, previous);
  row.appendChild(previous);
  row.appendChild(current);

  selectMedia(current, selectedMediaState.value?.type || "image");
  emitBodyPatch();
};

const splitSelectedMediaRow = () => {
  const current = selectedMediaElementRef.value;
  const row = current?.parentElement;
  if (!isMediaElement(current) || !isMediaRow(row) || !row.parentNode) {
    return;
  }

  const parent = row.parentNode;
  const children = Array.from(row.children);
  children.forEach((child) => {
    parent.insertBefore(child, row);
  });
  row.remove();

  selectMedia(current, selectedMediaState.value?.type || "image");
  emitBodyPatch();
};
</script>
